import { Link } from "react-router-dom";
import { useShopStore } from "../store/shop.store";
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Settings, Key, Save, Loader2, CheckCircle, ExternalLink, Store, Link2, Unlink, Clock } from "lucide-react";
import { apiClient } from "../lib/api";

export default function SettingsPage() {
  const queryClient = useQueryClient();
  const [openrouterKey, setOpenrouterKey] = useState("");
  const [lumirouteKey, setLumirouteKey] = useState("");
  const [anthropicKey, setAnthropicKey] = useState("");
  const [dhlKey, setDhlKey] = useState("");
  const [saved, setSaved] = useState(false);
  const [shopSyncInterval, setShopSyncInterval] = useState("24");

  const { data: settings, isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: () => apiClient.getSettings(),
  });

  useEffect(() => {
    if (settings) {
      if (settings.openrouter_api_key) setOpenrouterKey(settings.openrouter_api_key);
      if (settings.lumiroute_api_key) setLumirouteKey(settings.lumiroute_api_key);
      if (settings.anthropic_api_key) setAnthropicKey(settings.anthropic_api_key);
      if (settings.dhl_api_key) setDhlKey(settings.dhl_api_key);
      if (settings.shop_sync_interval_hours) setShopSyncInterval(settings.shop_sync_interval_hours);
    }
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: (data: Record<string, string>) => apiClient.updateSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["settings"] });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  function handleSave(e: React.FormEvent) {
    e.preventDefault();
    saveMutation.mutate({
      openrouter_api_key: openrouterKey,
      lumiroute_api_key: lumirouteKey,
      anthropic_api_key: anthropicKey,
      dhl_api_key: dhlKey,
      shop_sync_interval_hours: shopSyncInterval,
    });
  }

  // Canva connection
  const { data: canvaStatus } = useQuery({
    queryKey: ["canva-status"],
    queryFn: async () => {
      const backendUrl = import.meta.env.VITE_API_URL || "";
      const token = localStorage.getItem("erc_token") || "";
      const res = await fetch(`${backendUrl}/api/canva/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return { connected: false };
      const data = await res.json();
      return data.data as { connected: boolean };
    },
  });

  const isCanvaConnected = canvaStatus?.connected || false;

  async function handleConnectCanva() {
    try {
      const backendUrl = import.meta.env.VITE_API_URL || "";
      const token = localStorage.getItem("erc_token") || "";
      const res = await fetch(`${backendUrl}/api/canva/oauth/connect`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err.error || "Canva bağlantısı başlatılamadı");
        return;
      }
      const data = await res.json();
      window.location.href = data.data.url;
    } catch {
      alert("Canva bağlantı hatası");
    }
  }

  async function handleDisconnectCanva() {
    try {
      const backendUrl = import.meta.env.VITE_API_URL || "";
      const token = localStorage.getItem("erc_token") || "";
      await fetch(`${backendUrl}/api/canva/disconnect`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      queryClient.invalidateQueries({ queryKey: ["canva-status"] });
    } catch {
      alert("Canva bağlantı kesme hatası");
    }
  }

  // Ilk baglanti cagrisi yalnizca hic magaza yokken gosterilir
  const shopCount = useShopStore((s) => s.shops.length);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-2xl p-8">
        <div className="h-8 w-48 animate-pulse rounded bg-gray-200 mb-6" />
        <div className="h-48 animate-pulse rounded-xl bg-gray-100" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Settings className="h-6 w-6 text-gray-400" />
          Ayarlar
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          API anahtarları ve uygulama yapılandırması
        </p>
      </div>

      {/* Magaza yonetimi artik ayri sayfada — burada sadece yonlendirme */}
      <Link
        to="/my-shops"
        className="mb-6 flex items-center gap-3 rounded-xl border border-orange-200 bg-white p-4 shadow-sm transition-colors hover:bg-orange-50/40"
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-orange-50">
          <Store className="h-5 w-5 text-orange-600" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-900">
            Mağazalarım{shopCount > 0 ? ` (${shopCount})` : ""}
          </p>
          <p className="text-xs text-gray-500">
            Mağaza bağlama, kategori, pasife alma ve mağazaya özel üretim ayarları
          </p>
        </div>
        <span className="text-xs font-medium text-orange-600">Aç →</span>
      </Link>

      <form onSubmit={handleSave} className="space-y-6">
        {/* Canva Connect */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-start gap-3 mb-4">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-purple-50">
              <ExternalLink className="h-5 w-5 text-purple-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-semibold text-gray-900">Canva Entegrasyonu</h2>
              <p className="text-sm text-gray-500">
                Tasarım Editöründeki görselleri Canva'da açıp düzenle, sonra geri al.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {isCanvaConnected ? (
                <>
                  <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700">
                    <CheckCircle className="h-3 w-3" /> Bağlı
                  </span>
                  <button
                    type="button"
                    onClick={handleDisconnectCanva}
                    className="flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <Unlink className="h-3.5 w-3.5" />
                    Bağlantıyı Kes
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={handleConnectCanva}
                  className="flex items-center gap-1.5 rounded-lg bg-[#7B2FF2] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#6B21D6] transition-colors"
                >
                  <Link2 className="h-3.5 w-3.5" />
                  Canva'ya Bağlan
                </button>
              )}
            </div>
          </div>

          <div className="rounded-lg border border-purple-100 bg-purple-50 p-3 mb-3">
            <p className="text-xs text-purple-700">
              Bağlandığında Tasarım Editöründeki "Canva'da Aç" butonu görseli otomatik olarak Canva'ya yükler ve düzenlemek için açar.
            </p>
          </div>

          <a
            href="https://www.canva.dev/docs/connect/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700"
          >
            <ExternalLink className="h-3 w-3" />
            Canva Connect API Dokümantasyonu
          </a>
        </div>

        {/* OpenRouter API Key */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-start gap-3 mb-4">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-50">
              <Key className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">OpenRouter API</h2>
              <p className="text-sm text-gray-500">
                Listing Builder'da AI modelleri kullanmak için OpenRouter API anahtarı gereklidir.
              </p>
            </div>
          </div>

          <div className="mb-3">
            <label htmlFor="openrouter-key" className="block text-sm font-medium text-gray-700 mb-1">
              API Key
            </label>
            <input
              id="openrouter-key"
              type="password"
              value={openrouterKey}
              onChange={(e) => setOpenrouterKey(e.target.value)}
              placeholder="sk-or-v1-..."
              className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-gray-700 placeholder-gray-400 focus:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300"
            />
          </div>

          <a
            href="https://openrouter.ai/keys"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700"
          >
            <ExternalLink className="h-3 w-3" />
            OpenRouter'dan API anahtarı al
          </a>
        </div>

        {/* LumiRoute (OpenAI uyumlu gecit, BYOK): Studio / Toplu Studio gorsel modeli */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-start gap-3 mb-4">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-50">
              <Key className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">LumiRoute API</h2>
              <p className="text-sm text-gray-500">
                Takı Studio ve Toplu Studio'da “GPT Image 2 (LumiRoute)” modeli için. OpenAI anahtarınız LumiRoute'ta (BYOK) kayıtlı olmalı.
              </p>
            </div>
          </div>
          <div className="mb-3">
            <label htmlFor="lumiroute-key" className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
            <input id="lumiroute-key" type="password" value={lumirouteKey} onChange={(e) => setLumirouteKey(e.target.value)} placeholder="lr_..."
              className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-gray-700 placeholder-gray-400 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
          </div>
          <a href="https://lumirouter.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700">
            <ExternalLink className="h-3 w-3" /> LumiRoute
          </a>
        </div>

        {/* DHL Takip API (Shipment Tracking - Unified) */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-start gap-3 mb-4">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-yellow-50">
              <Key className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">DHL Takip API</h2>
              <p className="text-sm text-gray-500">
                Eklentinin Siparişler sekmesinde DHL SmartMail / eCommerce gönderilerinin durumunu okumak için. developer.dhl.com'da "Shipment Tracking - Unified" uygulamasının API Key'i (API Secret gerekmez).
              </p>
            </div>
          </div>
          <div className="mb-3">
            <label htmlFor="dhl-key" className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
            <input id="dhl-key" type="password" value={dhlKey} onChange={(e) => setDhlKey(e.target.value)} placeholder="DHL API Key"
              className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-gray-700 placeholder-gray-400 focus:border-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-200" />
          </div>
          <a href="https://developer.dhl.com/user/apps" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700">
            <ExternalLink className="h-3 w-3" /> DHL Developer · My Apps
          </a>
        </div>

        {/* Anthropic (Claude) API Key */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-start gap-3 mb-4">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-orange-50">
              <Key className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Claude API (Anthropic)</h2>
              <p className="text-sm text-gray-500">
                Rexven AI liste analizi Claude'u doğrudan Anthropic API ile çağırır. Boş ise OpenRouter kullanılır.
              </p>
            </div>
          </div>
          <div className="mb-3">
            <label htmlFor="anthropic-key" className="block text-sm font-medium text-gray-700 mb-1">
              API Key
            </label>
            <input
              id="anthropic-key"
              type="password"
              value={anthropicKey}
              onChange={(e) => setAnthropicKey(e.target.value)}
              placeholder="sk-ant-..."
              className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-gray-700 placeholder-gray-400 focus:border-orange-300 focus:outline-none focus:ring-1 focus:ring-orange-300"
            />
          </div>
          <a
            href="https://platform.claude.com/settings/keys"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700"
          >
            <ExternalLink className="h-3 w-3" />
            Claude Console'dan API anahtarı al
          </a>
        </div>

        {/* Shop Sync Interval */}
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-start gap-3 mb-4">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-50">
              <Clock className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Mağaza Otomatik Güncelleme</h2>
              <p className="text-sm text-gray-500">
                Takip edilen mağazaların Etsy API üzerinden otomatik güncelleme sıklığı.
                Sadece yeni veriler (istatistikler, yeni ürünler, son 48 saat yorumlar) çekilir.
              </p>
            </div>
          </div>

          <div className="mb-3">
            <label htmlFor="sync-interval" className="block text-sm font-medium text-gray-700 mb-1">
              Güncelleme Aralığı
            </label>
            <select
              id="sync-interval"
              value={shopSyncInterval}
              onChange={(e) => setShopSyncInterval(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm text-gray-700 focus:border-emerald-300 focus:outline-none focus:ring-1 focus:ring-emerald-300"
            >
              <option value="0">Devre Dışı (Manuel)</option>
              <option value="6">Her 6 saatte bir</option>
              <option value="12">Her 12 saatte bir</option>
              <option value="24">Günlük (24 saat)</option>
              <option value="48">Her 2 günde bir</option>
              <option value="168">Haftalık</option>
            </select>
            <p className="mt-1 text-xs text-gray-400">
              Etsy API rate limit'i korumak için sık aralık seçilmesi önerilmez. Varsayılan: Günlük.
            </p>
          </div>

          <div className="rounded-lg border border-amber-100 bg-amber-50 p-3">
            <p className="text-xs text-amber-700">
              <span className="font-medium">Neler güncellenir:</span> Mağaza istatistikleri (satış, favori, puan), aktif ürün listesi, son 48 saatin yorumları.
              Tüm yorumlar veya eski veriler tekrar çekilmez.
            </p>
          </div>
        </div>

        {/* Save button */}
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={saveMutation.isPending}
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Kaydet
          </button>
          {saved && (
            <span className="flex items-center gap-1 text-sm text-emerald-600">
              <CheckCircle className="h-4 w-4" />
              Kaydedildi
            </span>
          )}
          {saveMutation.isError && (
            <span className="text-sm text-red-600">
              Hata: {saveMutation.error?.message}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}
